import './resetPasswordPage.css';
import React, {Component} from "react";
import AuthenticationService from "../services/authenticationService";
import RedirectionService from "../services/redirectionService";
import {Navigate} from "react-router-dom";
import {ToastErrorLevel} from "../../App";
import {withRouter} from "./withRouter";
import LoadingSpinner from "./loadingSpinner";
import i18nService from "../../i18n/i18nService";

interface ResetPasswordState {
    token: string,
    newPassword: string,
    confirmPassword: string,
    error?: string,
    success: boolean,
    isLoading: boolean,
    navigation?: string,
}

interface ResetPasswordProps {
    toastGenerator: (body: string, level: ToastErrorLevel) => any,
}

class ResetPasswordPage extends Component<ResetPasswordProps, ResetPasswordState> {

    constructor(props: ResetPasswordProps) {
        super(props, {})
        // @ts-ignore
        const searchParams = new URLSearchParams(this.props.location?.search || '');
        const token = searchParams.get('token') || '';
        this.state = {
            token: token,
            newPassword: "",
            confirmPassword: "",
            success: false,
            isLoading: false,
        };
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleNewPasswordChange = this.handleNewPasswordChange.bind(this);
        this.handleConfirmPasswordChange = this.handleConfirmPasswordChange.bind(this);
    }

    async handleSubmit(event: any) {
        event.preventDefault();
        
        if (!this.state.token) {
            this.setState({error: "Invalid reset token. Please use the link from your email."});
            return;
        }
        
        if (!this.state.newPassword || !this.state.confirmPassword) {
            this.setState({error: "Please enter and confirm your new password"});
            return;
        }
        
        if (this.state.newPassword.length < 6) {
            this.setState({error: "Password must be at least 6 characters"});
            return;
        }
        
        if (this.state.newPassword !== this.state.confirmPassword) {
            this.setState({error: "Passwords do not match"});
            return;
        }
        
        this.setState({isLoading: true, error: undefined});
        
        await AuthenticationService.resetPassword(
            this.state.token,
            this.state.newPassword,
            async () => {
                this.setState({
                    success: true,
                    isLoading: false,
                });
                this.props.toastGenerator("Password reset successfully! You can now log in with your new password.", ToastErrorLevel.INFO);
                setTimeout(() => {
                    this.setState({
                        navigation: RedirectionService.redirectToLogin("/dashboard"),
                    });
                }, 2000);
            },
            async (error) => {
                this.setState({
                    error: error,
                    isLoading: false,
                });
                this.props.toastGenerator(error, ToastErrorLevel.ERROR);
            }
        );
    }

    handleNewPasswordChange(event: any) {
        this.setState({
            newPassword: event.target.value,
            error: undefined,
        });
    }

    handleConfirmPasswordChange(event: any) {
        this.setState({
            confirmPassword: event.target.value,
            error: undefined,
        });
    }

    render() {
        if (this.state.navigation) {
            return <Navigate to={this.state.navigation}/>
        }
        
        if (this.state.success) {
            return (
                <div className="ResetPasswordPage-Wrapper">
                    <div className="ResetPasswordPage-Container">
                        <h1 className="ResetPasswordPage-Title">{i18nService.t('auth.passwordResetSuccessful', 'Password Reset Successful')}</h1>
                        <p className="ResetPasswordPage-Success">{i18nService.t('auth.passwordResetRedirect', 'Your password has been reset successfully. Redirecting to login...')}</p>
                    </div>
                </div>
            );
        }
        
        return (
            <div className="ResetPasswordPage-Wrapper">
                <div className="ResetPasswordPage-Container">
                    {this.state.isLoading && (
                        <div className="ResetPasswordPage-Loading">
                            <LoadingSpinner size="large" message={i18nService.t('auth.resettingPassword', 'Resetting password...')} />
                        </div>
                    )}
                    {!this.state.isLoading && (
                    <>
                    <h1 className="ResetPasswordPage-Title">{i18nService.t('auth.resetPassword')}</h1>
                    <p className="ResetPasswordPage-Description">{i18nService.t('auth.resetPasswordEnterNew', 'Enter your new password below.')}</p>
                    
                    <form onSubmit={this.handleSubmit}>
                        <div className="ResetPasswordPage-InputGroup">
                            <input 
                                type="password" 
                                className="ResetPasswordPage-Input"
                                placeholder={i18nService.t('auth.newPassword', 'New password')} 
                                value={this.state.newPassword}
                                onChange={this.handleNewPasswordChange}
                                disabled={this.state.isLoading}
                                required
                                minLength={6}
                            />
                            <input 
                                type="password" 
                                className="ResetPasswordPage-Input"
                                placeholder={i18nService.t('auth.confirmNewPassword', 'Confirm new password')} 
                                value={this.state.confirmPassword}
                                onChange={this.handleConfirmPasswordChange}
                                disabled={this.state.isLoading}
                                required
                                minLength={6}
                            />
                        </div>
                        
                        {this.state.error && (
                            <div className="ResetPasswordPage-Error">{this.state.error}</div>
                        )}
                        
                        <div className="ResetPasswordPage-ButtonGroup">
                            <button 
                                type="submit"
                                className="ResetPasswordPage-Button"
                                disabled={this.state.isLoading || !this.state.newPassword || !this.state.confirmPassword}
                            >
                                {i18nService.t('auth.resetPassword')}
                            </button>
                        </div>
                    </form>
                    </>
                    )}
                </div>
            </div>
        );
    }
}

export default withRouter(ResetPasswordPage);

